<template>
  <v-container
    id="user-profile"
    fluid
    tag="section"
  >
    <v-row justify="center">
      <v-col
        cols="12"
        md="8"
      >
        <base-material-card>
          <template v-slot:heading>
            <div class="display-2 font-weight-light">
              Edit Profile
            </div>
          </template>

          <v-form @submit.prevent="updateProfile">
            <v-container class="py-0">
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="form.name"
                    label="Full Name"
                    class="purple-input"
                    :disabled="loading"
                    autocomplete="name"
                  />
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="form.email"
                    label="Email Address"
                    class="purple-input"
                    disabled
                    autocomplete="email"
                  />
                </v-col>

                <v-col cols="12">
                  <v-expansion-panels>
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        Reset Password
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row>
                          <v-col
                            cols="12"
                            md="6"
                          >
                            <v-text-field
                              v-model="form.oldPassword"
                              label="Current Password"
                              type="password"
                              class="purple-input"
                              :disabled="loading"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col
                            cols="12"
                            md="6"
                          >
                            <v-text-field
                              v-model="form.password"
                              label="New Password"
                              type="password"
                              class="purple-input"
                              :disabled="loading"
                              autocomplete="new-password"
                            />
                            <div v-if="form.password" class="password-strength mt-2">
                              <div class="d-flex align-center mb-1">
                                <v-chip
                                  :color="passwordStrength.level === 'Very Weak' ? 'error' : passwordStrength.level === 'Weak' ? 'warning' : 'success'"
                                  x-small
                                  class="mr-2"
                                >
                                  {{ passwordStrength.level }}
                                </v-chip>
                                <span v-if="!passwordStrength.valid" class="caption text-medium-emphasis">
                                  Must contain: {{ passwordStrength.messages.join(', ') }}
                                </span>
                                <span v-else class="caption success--text">
                                  Password meets requirements
                                </span>
                              </div>
                              <v-progress-linear
                                :value="passwordStrength.valid ? 100 : passwordStrength.hasMinLength ? 40 : 20"
                                :color="passwordStrength.level === 'Very Weak' ? 'error' : passwordStrength.level === 'Weak' ? 'warning' : 'success'"
                                height="4"
                                rounded
                              />
                            </div>
                          </v-col>
                          <v-col
                            cols="12"
                            md="6"
                          >
                            <v-text-field
                              v-model="form.confirmPassword"
                              label="Confirm Password"
                              type="password"
                              class="purple-input"
                              :disabled="loading"
                              :error-messages="passwordError"
                              autocomplete="new-password"
                            />
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>

                <v-col cols="12">
                  <v-chip-group>
                    <v-chip
                      v-for="role in user.roles"
                      :key="role"
                      label
                      disabled
                    >
                      {{ role }}
                    </v-chip>
                  </v-chip-group>
                </v-col>

                <v-col v-if="isSuperAdmin" cols="12">
                  <v-expansion-panels>
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        API Keys
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row>
                          <v-col cols="12">
                            <v-alert
                              v-if="newApiKey"
                              type="success"
                              class="mb-4"
                              dense
                            >
                              <div class="font-weight-bold mb-1">New API Key Created</div>
                              <div class="font-weight-regular mb-2">Copy this key now - you won't see it again:</div>
                              <div class="d-flex align-center mb-2">
                                <code class="d-block pa-2 bg-dark rounded flex-grow-1">{{ newApiKey }}</code>
                                <v-btn
                                  icon
                                  small
                                  class="ml-2"
                                  @click="copyToClipboard"
                                >
                                  <v-icon small>{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                </v-btn>
                              </div>
                              <v-btn
                                color="grey"
                                small
                                @click="newApiKey = null"
                              >
                                Done
                              </v-btn>
                            </v-alert>
                          </v-col>
                        </v-row>
                        <v-row align="center">
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="newKeyName"
                              label="Key Name (optional)"
                              placeholder="e.g., Payroll Integration"
                              class="purple-input"
                              :disabled="loadingApiKey"
                            />
                          </v-col>
                          <v-col cols="12" md="auto">
                            <v-btn
                              color="primary"
                              :loading="loadingApiKey"
                              :disabled="!newKeyName"
                              @click="createApiKey"
                            >
                              Create Key
                            </v-btn>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <v-divider class="mb-4" />
                            <div v-if="apiKeys.length === 0" class="text-center grey--text py-4">
                              No API keys yet. Create one to integrate with external services.
                            </div>
                            <v-list v-else dense>
                              <v-list-item
                                v-for="key in apiKeys"
                                :key="key.id"
                                class="mb-2 rounded"
                                style="border: 1px solid #e0e0e0"
                              >
                                <v-list-item-content>
                                  <v-list-item-title class="font-weight-medium">
                                    {{ key.name || 'Unnamed Key' }}
                                  </v-list-item-title>
                                  <v-list-item-subtitle>
                                    Prefix: <code>{{ key.keyPrefix }}****</code>
                                    <span v-if="key.lastUsedAt" class="ml-2">
                                      • Last used: {{ formatDate(key.lastUsedAt) }}
                                    </span>
                                  </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                  <v-btn
                                    icon
                                    small
                                    color="error"
                                    :loading="revokingId === key.id"
                                    @click="revokeApiKey(key.id)"
                                  >
                                    <v-icon small>mdi-delete</v-icon>
                                  </v-btn>
                                </v-list-item-action>
                              </v-list-item>
                            </v-list>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>

                <v-col cols="12">
                  <v-chip-group>
                    <v-chip
                      v-for="team in user.teams"
                      :key="team"
                      label
                      disabled
                    >
                      {{ team.name }}
                    </v-chip>
                  </v-chip-group>
                </v-col>

                <v-col
                  cols="12"
                  class="text-right"
                >
                  <v-btn
                    color="grey"
                    class="mr-2"
                    @click="$router.push('/')"
                  >
                    Back
                  </v-btn>
                  <v-btn
                    color="success"
                    :loading="loading"
                    type="submit"
                  >
                    Update Profile
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </base-material-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import { mapState } from 'vuex'
  import { M_UPDATE_USER, Q_GET_API_KEYS, M_CREATE_API_KEY, M_REVOKE_API_KEY } from '@/gql/user'

  export default {
    name: 'UserProfile',

    data: () => ({
      loading: false,
      loadingApiKey: false,
      revokingId: null,
      newKeyName: '',
      newApiKey: null,
      copied: false,
      apiKeys: [],
      form: {
        name: '',
        email: '',
        oldPassword: '',
        password: '',
        confirmPassword: '',
      },
    }),

    computed: {
      ...mapState(['user']),
      isSuperAdmin () {
        return this.user?.roles?.includes('super-admin')
      },
      passwordError () {
        if (!this.form.password || !this.form.confirmPassword) return ''
        return this.form.password !== this.form.confirmPassword
          ? 'Passwords do not match'
          : ''
      },
      passwordStrength () {
        const p = this.form.password
        if (!p) return { valid: true, level: '', messages: [] }
        
        const hasUpper = /[A-Z]/.test(p)
        const hasLower = /[a-z]/.test(p)
        const hasNumber = /[0-9]/.test(p)
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(p)
        const hasMinLength = p.length >= 8
        
        const messages = []
        if (!hasMinLength) messages.push('At least 8 characters')
        if (!hasUpper) messages.push('Capital letter')
        if (!hasLower) messages.push('Lowercase letter')
        if (!hasNumber) messages.push('Number')
        if (!hasSpecial) messages.push('Special character (!@#$%^&*)')
        
        const valid = hasUpper && hasLower && hasNumber && hasSpecial && hasMinLength
        const level = valid ? 'Medium' : messages.length <= 2 ? 'Weak' : 'Very Weak'
        
        return { valid, level, messages, hasUpper, hasLower, hasNumber, hasSpecial, hasMinLength }
      },
    },

    watch: {
      user: {
        immediate: true,
        handler (user) {
          if (user) {
            this.form.name = user.name
            this.form.email = user.email
            if (this.isSuperAdmin) {
              this.fetchApiKeys()
            }
          }
        },
      },
    },

    methods: {
      formatDate (dateStr) {
        if (!dateStr) return ''
        return new Date(dateStr).toLocaleString()
      },
      async copyToClipboard () {
        try {
          await navigator.clipboard.writeText(this.newApiKey)
          this.copied = true
          this.$toast.success('Copied to clipboard')
          setTimeout(() => { this.copied = false }, 2000)
        } catch (error) {
          this.$toast.error('Failed to copy')
        }
      },
      async fetchApiKeys () {
        try {
          const { data } = await this.$apollo.query({
            query: Q_GET_API_KEYS,
            fetchPolicy: 'no-cache',
          })
          this.apiKeys = data.myApiKeys || []
        } catch (error) {
          console.error('Error fetching API keys:', error)
        }
      },
      async createApiKey () {
        this.loadingApiKey = true
        try {
          const { data } = await this.$apollo.mutate({
            mutation: M_CREATE_API_KEY,
            variables: { name: this.newKeyName || null },
          })
          this.newApiKey = data.createApiKey.rawKey
          this.newKeyName = ''
          await this.fetchApiKeys()
          this.$toast.success('API key created successfully')
        } catch (error) {
          console.error('Error creating API key:', error)
          this.$toast.error('Failed to create API key')
        } finally {
          this.loadingApiKey = false
        }
      },
      async revokeApiKey (id) {
        this.revokingId = id
        try {
          await this.$apollo.mutate({
            mutation: M_REVOKE_API_KEY,
            variables: { id },
          })
          await this.fetchApiKeys()
          this.$toast.success('API key revoked')
        } catch (error) {
          console.error('Error revoking API key:', error)
          this.$toast.error('Failed to revoke API key')
        } finally {
          this.revokingId = null
        }
      },
      async updateProfile () {
        if (this.form.oldPassword && !this.form.password) {
          this.$toast.error('New password is required when providing current password')
          return
        }

        if (this.form.password && !this.form.oldPassword) {
          this.$toast.error('Current password is required to set a new password')
          return
        }

        if (this.form.password && !this.passwordStrength.valid) {
          this.$toast.error(`Password is too weak. Required: ${this.passwordStrength.messages.join(', ')}`)
          return
        }

        if (this.form.password && this.form.password !== this.form.confirmPassword) {
          return
        }

        this.loading = true
        try {
          const variables = {
            id: parseInt(this.user.id),
            name: this.form.name,
            ...(this.form.password ? { password: this.form.password, oldPassword: this.form.oldPassword } : {}),
          }

          await this.$apollo.mutate({
            mutation: M_UPDATE_USER,
            variables,
          })

          this.$store.commit('SET_USER', {
            ...this.user,
            name: this.form.name,
          })

          this.form.oldPassword = ''
          this.form.password = ''
          this.form.confirmPassword = ''

          this.$toast.success('Profile updated successfully')
          // redirect to home
          this.$router.push('/')
        } catch (error) {
          console.error('Error updating profile:', error)
          const message = error.graphQLErrors?.[0]?.message || error.message
          if (message === 'OLD_PASSWORD_INCORRECT') {
            this.$toast.error('Current password is incorrect')
          } else if (message === 'OLD_PASSWORD_REQUIRED') {
            this.$toast.error('Current password is required to set a new password')
          } else {
            this.$toast.error('Failed to update profile')
          }
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>
